import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabase';
import {
  loadTodayLogs, loadRecentLogs,
  logFood, logWater, logExercise, deleteLog,
  generateHealthInsight,
  sumWaterMl, sumExerciseMin, countMeals,
  describeLog, formatRelativeTime,
  DEFAULT_GOALS, MEAL_TYPES, INTENSITY_LEVELS, EXERCISE_SUGGESTIONS, WATER_QUICK_AMOUNTS,
} from '../healthData';
import Card from '../../design-system/Card';
import EmptyState from '../../components/EmptyState';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import ProgressCard from '../../components/ProgressCard';
import { colors, spacing, radius, fontStack } from '../../design-system/tokens';

/**
 * /system/health — Phase 4D rebuild.
 * Same data layer (healthData.js), light-theme UI per spec.
 */
export default function Health() {
  const [userId, setUserId] = useState(null);
  const [todayLogs, setTodayLogs] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [view, setView] = useState('home'); // home | log-food | log-water | log-exercise | history | insight
  const [insightResult, setInsightResult] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState(null);

  const [toast, setToast] = useState(null);

  /* MOUNT */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data: authData } = await supabase.auth.getUser();
        const user = authData?.user;
        if (cancelled) return;
        if (!user) { setLoadError('Not signed in'); return; }
        setUserId(user.id);
        const [t, r] = await Promise.all([
          loadTodayLogs(user.id).catch(() => []),
          loadRecentLogs(user.id, 7).catch(() => []),
        ]);
        if (cancelled) return;
        setTodayLogs(t);
        setRecentLogs(r);
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'Could not load Health');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const refreshAll = async () => {
    if (!userId) return;
    const [t, r] = await Promise.all([
      loadTodayLogs(userId).catch(() => []),
      loadRecentLogs(userId, 7).catch(() => []),
    ]);
    setTodayLogs(t);
    setRecentLogs(r);
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 1800);
  };

  /* HANDLERS */

  const handleLogFood = async (data) => {
    try { await logFood({ user_id: userId, ...data }); await refreshAll(); setView('home'); showToast('Meal logged'); }
    catch (err) { showToast(err.message || 'Save failed', 'error'); }
  };

  const handleLogWater = async (ml) => {
    try { await logWater({ user_id: userId, amount_ml: ml }); await refreshAll(); showToast(`+${ml}ml logged`); }
    catch (err) { showToast(err.message || 'Save failed', 'error'); }
  };

  const handleLogExercise = async (data) => {
    try { await logExercise({ user_id: userId, ...data }); await refreshAll(); setView('home'); showToast('Exercise logged'); }
    catch (err) { showToast(err.message || 'Save failed', 'error'); }
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('Remove this log?')) return;
    try { await deleteLog(logId); await refreshAll(); showToast('Removed'); }
    catch (err) { showToast(err.message || 'Delete failed', 'error'); }
  };

  const handleGenerateInsight = async () => {
    setView('insight');
    setInsightResult(null);
    setInsightError(null);
    setInsightLoading(true);
    try {
      const summary = {
        meals_count: countMeals(todayLogs),
        water_ml: sumWaterMl(todayLogs),
        exercise_min: sumExerciseMin(todayLogs),
      };
      const result = await generateHealthInsight({ recent: recentLogs, todaySummary: summary, energyToday: null });
      setInsightResult(result);
    } catch (err) {
      setInsightError(err.message || 'Could not generate insight');
    } finally {
      setInsightLoading(false);
    }
  };

  const meals = useMemo(() => countMeals(todayLogs), [todayLogs]);
  const waterMl = useMemo(() => sumWaterMl(todayLogs), [todayLogs]);
  const exerciseMin = useMemo(() => sumExerciseMin(todayLogs), [todayLogs]);

  /* RENDER */

  if (loadError && !userId) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <ErrorState title="Couldn't load Health" message={loadError} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', fontFamily: fontStack }}>
      <Header view={view} onBack={() => setView('home')} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg, marginTop: spacing.lg }}>
        {view === 'home' && (
          <HomeView
            loading={loading}
            error={loadError}
            meals={meals}
            waterMl={waterMl}
            exerciseMin={exerciseMin}
            recentLogs={recentLogs}
            onQuickWater={() => handleLogWater(250)}
            onOpenFood={() => setView('log-food')}
            onOpenWater={() => setView('log-water')}
            onOpenExercise={() => setView('log-exercise')}
            onOpenHistory={() => setView('history')}
            onOpenInsight={handleGenerateInsight}
            onDeleteLog={handleDeleteLog}
            onRetry={refreshAll}
          />
        )}
        {view === 'log-food' && (
          <LogFoodView onSave={handleLogFood} onCancel={() => setView('home')} />
        )}
        {view === 'log-water' && (
          <LogWaterView onSave={handleLogWater} onDone={() => setView('home')} />
        )}
        {view === 'log-exercise' && (
          <LogExerciseView onSave={handleLogExercise} onCancel={() => setView('home')} />
        )}
        {view === 'history' && (
          <HistoryView recentLogs={recentLogs} onDelete={handleDeleteLog} />
        )}
        {view === 'insight' && (
          <InsightView
            loading={insightLoading}
            error={insightError}
            result={insightResult}
            onRetry={handleGenerateInsight}
          />
        )}
      </div>

      {toast && <Toast {...toast} />}
    </div>
  );
}

/* ================================================================
   Header
   ================================================================ */

function Header({ view, onBack }) {
  const inSubView = view !== 'home';
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', month: 'long', day: 'numeric' });
  return (
    <header style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <a
          href="/system"
          onClick={(e) => { if (inSubView) { e.preventDefault(); onBack(); } }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            color: colors.textSecondary,
            textDecoration: 'none',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          ← {inSubView ? 'Back to Health' : 'System'}
        </a>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, color: colors.textPrimary, letterSpacing: '-0.01em' }}>
          {viewTitle(view)}
        </h1>
        {!inSubView && (
          <span style={{ marginLeft: 'auto', fontSize: 13, color: colors.textSecondary }}>{today}</span>
        )}
      </div>
      {!inSubView && (
        <p style={{ margin: 0, color: colors.textSecondary, fontSize: 14 }}>
          A calmer way to keep an eye on the basics.
        </p>
      )}
    </header>
  );
}

function viewTitle(v) {
  return {
    home: 'Health',
    'log-food': 'Log a meal',
    'log-water': 'Log water',
    'log-exercise': 'Log exercise',
    history: 'History',
    insight: 'Health Insight',
  }[v] || 'Health';
}

/* ================================================================
   Home view
   ================================================================ */

function HomeView({
  loading, error,
  meals, waterMl, exerciseMin, recentLogs,
  onQuickWater, onOpenFood, onOpenWater, onOpenExercise,
  onOpenHistory, onOpenInsight, onDeleteLog, onRetry,
}) {
  return (
    <>
      {/* Three progress cards */}
      <div className="health-progress-grid">
        {loading && recentLogs.length === 0 ? (
          <>
            <LoadingState rows={3} />
            <LoadingState rows={3} />
            <LoadingState rows={3} />
          </>
        ) : (
          <>
            <ProgressCard
              label="Food"
              icon={<UtensilsIcon />}
              value={meals}
              unit={`of ${DEFAULT_GOALS.meals} meals`}
              actual={meals}
              goal={DEFAULT_GOALS.meals}
              accent={colors.accentEnergy}
              cta={{ label: '+ Log meal', onClick: onOpenFood }}
            />
            <ProgressCard
              label="Water"
              icon={<DropIcon />}
              value={waterMl}
              unit={`of ${DEFAULT_GOALS.water_ml}ml`}
              actual={waterMl}
              goal={DEFAULT_GOALS.water_ml}
              accent={colors.accentEnergy}
              cta={{ label: '+ 250ml', onClick: onQuickWater }}
            />
            <ProgressCard
              label="Exercise"
              icon={<HeartPulseIcon />}
              value={exerciseMin}
              unit={`of ${DEFAULT_GOALS.exercise_min} min`}
              actual={exerciseMin}
              goal={DEFAULT_GOALS.exercise_min}
              accent={colors.accentEnergy}
              cta={{ label: '+ Log exercise', onClick: onOpenExercise }}
            />
          </>
        )}
        <style>{`
          .health-progress-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
          }
          @media (max-width: 1024px) { .health-progress-grid { grid-template-columns: repeat(3, 1fr); } }
          @media (max-width: 768px)  { .health-progress-grid { grid-template-columns: 1fr; } }
        `}</style>
      </div>

      {/* Today's summary */}
      <Card padding={spacing.lg}>
        <SectionTitle>Today's Summary</SectionTitle>
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SummaryRow label="Meals" value={`${meals} / ${DEFAULT_GOALS.meals}`}
                      pct={DEFAULT_GOALS.meals ? Math.round((meals / DEFAULT_GOALS.meals) * 100) : 0} />
          <SummaryRow label="Water" value={`${waterMl}ml / ${DEFAULT_GOALS.water_ml}ml`}
                      pct={DEFAULT_GOALS.water_ml ? Math.round((waterMl / DEFAULT_GOALS.water_ml) * 100) : 0} />
          <SummaryRow label="Exercise" value={`${exerciseMin}min / ${DEFAULT_GOALS.exercise_min}min`}
                      pct={DEFAULT_GOALS.exercise_min ? Math.round((exerciseMin / DEFAULT_GOALS.exercise_min) * 100) : 0} />
        </div>
      </Card>

      {/* Recent activity */}
      <Card padding={spacing.lg}>
        <SectionTitle>Recent Activity</SectionTitle>
        {loading && recentLogs.length === 0 ? (
          <div style={{ marginTop: 12 }}><LoadingState rows={4} /></div>
        ) : recentLogs.length === 0 ? (
          <EmptyState
            title="No logs yet"
            message="Start with one of the cards above."
          />
        ) : (
          <ul style={{ listStyle: 'none', margin: '12px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {recentLogs.slice(0, 12).map((log) => (
              <ActivityRow key={log.id} log={log} onDelete={onDeleteLog} />
            ))}
          </ul>
        )}
      </Card>

      {error && (
        <ErrorState title="Some data couldn't load" message={error} onRetry={onRetry} inline />
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <button type="button" onClick={onOpenHistory} style={secondaryBtnStyle}>View History</button>
        <button type="button" onClick={onOpenInsight} style={secondaryBtnStyle}>Get Health Insight</button>
      </div>
    </>
  );
}

function SummaryRow({ label, value, pct }) {
  const fill = Math.min(100, pct || 0);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 13, color: colors.textPrimary, fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: 12, color: colors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
          {value} · {fill}%
        </span>
      </div>
      <div style={{ height: 4, borderRadius: 999, backgroundColor: 'var(--bg-hover)', overflow: 'hidden' }}>
        <div style={{ height: 4, width: `${fill}%`, backgroundColor: colors.accentEnergy, borderRadius: 999, transition: 'width 240ms ease' }} />
      </div>
    </div>
  );
}

function ActivityRow({ log, onDelete }) {
  const [hover, setHover] = useState(false);
  return (
    <li
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 12px',
        borderRadius: radius.sm,
        backgroundColor: hover ? 'var(--bg-hover)' : 'transparent',
        transition: 'background-color 120ms ease',
      }}
    >
      <span style={{ fontSize: 16 }} aria-hidden="true">{logGlyph(log.log_type)}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: colors.textPrimary, lineHeight: 1.3 }}>{describeLog(log)}</div>
        <div style={{ fontSize: 11, color: colors.textSecondary }}>{formatRelativeTime(log.logged_at)}</div>
      </div>
      <button
        type="button"
        onClick={() => onDelete(log.id)}
        aria-label="Remove log"
        style={{
          width: 28,
          height: 28,
          padding: 0,
          border: 'none',
          background: 'transparent',
          borderRadius: 999,
          cursor: 'pointer',
          color: colors.textSecondary,
          opacity: hover ? 1 : 0,
          fontSize: 16,
          fontFamily: fontStack,
        }}
      >
        ×
      </button>
    </li>
  );
}

function logGlyph(type) {
  if (type === 'water') return '💧';
  if (type === 'exercise') return '🏃';
  return '🍽️';
}

/* ================================================================
   Log forms
   ================================================================ */

function LogFoodView({ onSave, onCancel }) {
  const [mealType, setMealType] = useState('lunch');
  const [description, setDescription] = useState('');
  const [calories, setCalories] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!description.trim()) { setError('Add a quick description'); return; }
    setSaving(true); setError('');
    try {
      await onSave({
        meal_type: mealType,
        description: description.trim(),
        calories: calories ? Number(calories) : undefined,
      });
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card padding={spacing.lg}>
      <SectionTitle>Log a meal</SectionTitle>
      <form onSubmit={submit} style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Meal">
          <select value={mealType} onChange={(e) => setMealType(e.target.value)} style={inputStyle}>
            {MEAL_TYPES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </Field>
        <Field label="Description">
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 160))}
            placeholder="e.g., chicken salad with avocado"
            maxLength={160}
            style={inputStyle}
            required
          />
        </Field>
        <Field label="Calories (optional)">
          <input
            type="number"
            inputMode="numeric"
            min="0"
            value={calories}
            onChange={(e) => setCalories(e.target.value)}
            placeholder="—"
            style={inputStyle}
          />
        </Field>
        {error && <ErrorState inline title="Couldn't save" message={error} />}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" onClick={onCancel} style={secondaryBtnStyle}>Cancel</button>
          <button type="submit" disabled={saving} style={{ ...primaryBtnStyle, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Card>
  );
}

function LogWaterView({ onSave, onDone }) {
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (ml) => {
    if (busy) return;
    setBusy(true);
    try { await onSave(ml); } finally { setBusy(false); }
  };
  return (
    <Card padding={spacing.lg}>
      <SectionTitle>Log water</SectionTitle>
      <p style={{ margin: '4px 0 16px', fontSize: 13, color: colors.textSecondary }}>
        Quick add or enter your own amount.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {WATER_QUICK_AMOUNTS.map((ml) => (
          <button
            key={ml}
            type="button"
            onClick={() => submit(ml)}
            disabled={busy}
            style={{
              ...secondaryBtnStyle,
              minHeight: 44,
              padding: '10px 18px',
            }}
          >
            +{ml}ml
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="number"
          inputMode="numeric"
          min="1"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          placeholder="Custom (ml)"
          style={{ ...inputStyle, width: 160 }}
        />
        <button
          type="button"
          disabled={!custom || busy}
          onClick={() => submit(Number(custom))}
          style={{ ...primaryBtnStyle, opacity: !custom || busy ? 0.6 : 1 }}
        >
          Save
        </button>
        <button type="button" onClick={onDone} style={secondaryBtnStyle}>Done</button>
      </div>
    </Card>
  );
}

function LogExerciseView({ onSave, onCancel }) {
  const [activity, setActivity] = useState('Walking');
  const [duration, setDuration] = useState(20);
  const [intensity, setIntensity] = useState('medium');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!duration || duration <= 0) { setError('Duration must be at least 1 minute'); return; }
    setSaving(true); setError('');
    try {
      await onSave({ activity, duration_min: Number(duration), intensity });
    } catch (err) { setError(err.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Card padding={spacing.lg}>
      <SectionTitle>Log exercise</SectionTitle>
      <form onSubmit={submit} style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Activity">
          <input
            type="text"
            value={activity}
            list="exercise-options"
            onChange={(e) => setActivity(e.target.value.slice(0, 60))}
            placeholder="e.g., Running"
            maxLength={60}
            style={inputStyle}
          />
          <datalist id="exercise-options">
            {EXERCISE_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
          </datalist>
        </Field>
        <Field label="Duration (min)">
          <input
            type="number"
            inputMode="numeric"
            min="1"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label="Intensity">
          <select value={intensity} onChange={(e) => setIntensity(e.target.value)} style={inputStyle}>
            {INTENSITY_LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </Field>
        {error && <ErrorState inline title="Couldn't save" message={error} />}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" onClick={onCancel} style={secondaryBtnStyle}>Cancel</button>
          <button type="submit" disabled={saving} style={{ ...primaryBtnStyle, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Card>
  );
}

/* ================================================================
   History view
   ================================================================ */

function HistoryView({ recentLogs, onDelete }) {
  // Group logs by date
  const byDate = useMemo(() => {
    const map = {};
    for (const log of recentLogs || []) {
      const d = String(log.date || '').slice(0, 10);
      if (!d) continue;
      if (!map[d]) map[d] = [];
      map[d].push(log);
    }
    return map;
  }, [recentLogs]);

  const dates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));

  return (
    <Card padding={spacing.lg}>
      <SectionTitle>History · last 7 days</SectionTitle>
      {dates.length === 0 ? (
        <EmptyState message="No logs in the last week yet." />
      ) : (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 18 }}>
          {dates.map((d) => (
            <div key={d}>
              <div style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, marginBottom: 6, letterSpacing: '0.04em' }}>
                {new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'long', month: 'short', day: 'numeric' })}
              </div>
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {byDate[d].map((log) => <ActivityRow key={log.id} log={log} onDelete={onDelete} />)}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ================================================================
   Insight view
   ================================================================ */

function InsightView({ loading, error, result, onRetry }) {
  return (
    <Card padding={spacing.lg} accentColor={colors.accentSystem}>
      <SectionTitle>Health Insight</SectionTitle>
      {loading ? (
        <div style={{ marginTop: 12 }}><LoadingState rows={4} /></div>
      ) : error ? (
        <div style={{ marginTop: 12 }}>
          <ErrorState title="Couldn't generate insight" message={error} onRetry={onRetry} inline />
        </div>
      ) : !result ? (
        <EmptyState message="No insight yet — generate one above." />
      ) : (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <BulletList title="Observations" items={result.observations} />
          <BulletList title="Suggestions" items={result.suggestions} />
        </div>
      )}
    </Card>
  );
}

/* ================================================================
   Shared bits
   ================================================================ */

function SectionTitle({ children }) {
  return (
    <h3 style={{
      margin: 0,
      fontSize: 14,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: colors.textSecondary,
      fontWeight: 600,
      fontFamily: fontStack,
    }}>{children}</h3>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontFamily: fontStack }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function BulletList({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: colors.textSecondary, fontWeight: 600, marginBottom: 8 }}>
        {title}
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((it, i) => (
          <li key={i} style={{ fontSize: 14, color: colors.textPrimary, lineHeight: 1.5, paddingLeft: 12, borderLeft: `2px solid ${colors.border}` }}>
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Toast({ message, type }) {
  const isError = type === 'error';
  return (
    <div role="status" style={{
      position: 'fixed',
      bottom: 24,
      left: '50%',
      transform: 'translateX(-50%)',
      padding: '10px 18px',
      borderRadius: 999,
      backgroundColor: isError ? 'var(--danger)' : colors.textPrimary,
      color: '#fff',
      fontSize: 13,
      fontWeight: 500,
      fontFamily: fontStack,
      boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
      zIndex: 100,
    }}>{message}</div>
  );
}

/* ---- icons ---- */
function UtensilsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 2v7a3 3 0 0 0 6 0V2" />
      <path d="M6 2v20" />
      <path d="M18 2c-2 0-4 2-4 5v6h2v9" />
    </svg>
  );
}
function DropIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2.7s5 5.5 5 10.3a5 5 0 1 1-10 0c0-4.8 5-10.3 5-10.3z" />
    </svg>
  );
}
function HeartPulseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 14c1.5-1.5 3-3.3 3-5.5A4.5 4.5 0 0 0 17.5 4c-1.8 0-3 1-3.5 2-.5-1-1.7-2-3.5-2A4.5 4.5 0 0 0 6 8.5C6 12.5 12 17 12 17s.5-.4 1.5-1.2" />
      <path d="M3 12h4l2-3 4 6 2-3h6" />
    </svg>
  );
}

/* ---- shared button styles ---- */

const primaryBtnStyle = {
  padding: '10px 18px',
  minHeight: 40,
  borderRadius: 999,
  border: 'none',
  background: colors.primary,
  color: '#fff',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: fontStack,
  cursor: 'pointer',
};

const secondaryBtnStyle = {
  padding: '10px 16px',
  minHeight: 40,
  borderRadius: 999,
  background: 'transparent',
  border: `1px solid ${colors.primary}`,
  color: colors.primary,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: fontStack,
  cursor: 'pointer',
};

const inputStyle = {
  padding: '10px 12px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: 14,
  fontFamily: fontStack,
  color: colors.textPrimary,
  background: '#fff',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};
